import apiClient from '../client';
import ENDPOINTS from '../endpoints';

/* Master Calendar days off are NoSchool notes — the same documents the Sprout
   app's class calendars read (every class under the school sees a note saved
   on the school network). The app marks the day with a 🚫 row and no time, so
   a day off needs nothing but a title and a date, plus a last day for a break
   like Thanksgiving week. Listing is the events list's job: it unions the
   school's days off in with the network's events (see events.service). */

const pad = (n) => String(n).padStart(2, '0');
const localDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/* eventDate is date-only: the app sends "yyyy-mm-dd" and the server stores it
   as UTC midnight, so the day is read off the string — local parts would call
   it the day before anywhere west of Greenwich. */
const dateOnly = (v) => {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

/* startDateTime/endDateTime are real instants (local midnight and 23:59:59 on
   save), so their day comes from local parts, the way event times do. */
const instantDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : localDate(d);
};

export function normalizeNoSchoolDay(n) {
  const date = dateOnly(n?.eventDate) || instantDate(n?.startDateTime);
  const endDate = instantDate(n?.endDateTime);
  return {
    id: n?._id || n?.id || '',
    networkId: n?.networkId || '',
    title: n?.title || '',
    date,
    /* '' for a single day — only a break longer than a day carries a last day */
    endDate: endDate && endDate > date ? endDate : '',
    /* what the calendar rows and the form branch on */
    mode: 'noSchool',
    eventType: n?.eventType || 'NoSchool',
  };
}

/* A break spans the first day's midnight to the last day's 23:59:59. The
   server validates endDateTime against startDateTime, so they travel together. */
const rangeInstants = (date, endDate) => ({
  startDateTime: new Date(`${date}T00:00:00`).toISOString(),
  endDateTime: new Date(`${endDate}T23:59:59`).toISOString(),
});

/* { status, data: { note } | { data: [...] }, message } — the client already
   unwrapped the axios response, this unwraps the envelope. */
const unwrapNote = (res) => {
  const data = res?.data ?? res;
  return data?.note || data?.data || data;
};

const noSchoolService = {
  /* endDate '' = a single day. */
  create: async ({ networkId, title, date, endDate }) => {
    const res = await apiClient.post(ENDPOINTS.NOTE.ADD, {
      networkId,
      eventDate: date,
      eventType: 'NoSchool',
      title,
      ...(endDate ? rangeInstants(date, endDate) : {}),
    });
    return normalizeNoSchoolDay(unwrapNote(res));
  },

  /* Sends the whole day back. Null start/end turns a break back into a single
     day — the edit route accepts null where the add route has no field at all. */
  update: async ({ id, title, date, endDate }) => {
    const res = await apiClient.post(ENDPOINTS.NOTE.EDIT, {
      id,
      eventDate: date,
      title,
      ...(endDate ? rangeInstants(date, endDate) : { startDateTime: null, endDateTime: null }),
    });
    return normalizeNoSchoolDay(unwrapNote(res));
  },

  /* Soft delete, scoped to the network — 404 if the day isn't on it. */
  remove: (networkId, id) => apiClient.post(ENDPOINTS.NOTE.DELETE, { id, networkId }),
};

export default noSchoolService;
